import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Button, Card, HStack, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { useOpportunityCounts } from '@src/components/sco/Advisors/hooks/useOpportunityCounts';
import { OpportunitiesAccessGate, OpportunitiesLayout } from '@src/components/sco/Advisors/layout/OpportunitiesLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { cleanErrorMessage, formatWhen, personName } from '@src/components/sco/Advisors/utils/sessionFormat';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { RESPOND_TO_BOARD_INVITE } from '@src/gql/mutations/RespondToBoardInvite';
import { ADVISOR_INVITED_ENGAGEMENTS, ADVISOR_OPPORTUNITY_COUNTS } from '@src/gql/queries/AdvisorOpportunities';
import { ADVISORS_ATTENTION_REFETCH } from '@src/gql/queries/AdvisorsAttention';
import { ScoAdvisorEngagement, ScoAdvisorEngagementCollection } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useState } from 'react';
const PAGE_SIZE = 20;
type InvitedData = {
  advisorEngagements?: ScoAdvisorEngagementCollection | null;
};
const InvitesBody = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const [actionError, setActionError] = useState<string | null>(null);
  /** Track the specific invite in flight so only its buttons spin. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const listVariables = {
    pageSize: PAGE_SIZE,
    page: 1
  };
  const query = useQuery<InvitedData>(ADVISOR_INVITED_ENGAGEMENTS, {
    variables: listVariables,
    fetchPolicy: 'network-only'
  });
  const firstPageInvited = (query.data?.advisorEngagements?.items ?? []).filter((item): item is ScoAdvisorEngagement => !!item?.advisorEngagementId);
  const {
    items: invited,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore({
    fetchMore: query.fetchMore,
    field: 'advisorEngagements',
    firstPageItems: firstPageInvited,
    hasNextPage: query.data?.advisorEngagements?.pagination?.hasNextPage
  });
  const [respondToBoardInvite] = useMutation(RESPOND_TO_BOARD_INVITE, {
    refetchQueries: [{
      query: ADVISOR_INVITED_ENGAGEMENTS,
      variables: listVariables
    }, {
      query: ADVISOR_OPPORTUNITY_COUNTS
    }, ADVISORS_ATTENTION_REFETCH]
  });
  const handleResponse = async (engagementId: string, action: 'ACCEPT' | 'DECLINE') => {
    if (action === 'DECLINE' && !window.confirm('Decline this board seat?')) {
      return;
    }
    setActionError(null);
    setBusyId(engagementId);
    try {
      await respondToBoardInvite({
        variables: {
          input: {
            advisorEngagementId: engagementId,
            action
          }
        }
      });
      if (action === 'ACCEPT') {
        const engagement = invited.find(item_0 => item_0.advisorEngagementId === engagementId);
        const founderUserId = engagement?.founderUserId || engagement?.founderUser?.userId;
        void router.push(paths.withQuery(paths.inbox, {
          founderUserId: founderUserId || undefined
        }));
      }
    } catch (err) {
      setActionError(cleanErrorMessage(err, 'Could not update that invite.'));
    } finally {
      setBusyId(null);
    }
  };
  return <>
      {actionError && <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
          {actionError}
        </Text>}

      <AdvisorsQueryState error={query.error} loading={query.loading && !query.data}>
        {invited.length === 0 ? <Card.Root variant={'subtle'}>
            <Card.Body>
              <VStack align={'stretch'} gap={2}>
                <Text color={'colorPalette.fg'} fontWeight={'semibold'}>
                  No pending invites right now.
                </Text>
                <Text color={'colorPalette.fg/50'} fontSize={'xs'}>
                  Founders who invite you to a board seat show up here with their reason for reaching out.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root> : <VStack align={'stretch'} gap={3}>
            {invited.map(engagement_0 => {
          const id = engagement_0.advisorEngagementId as string;
          const isBusy = busyId === id;
          return <Card.Root key={id} variant={'subtle'}>
                  <Card.Body gap={3}>
                    <HStack align={'start'} justify={'space-between'} wrap={'wrap'}>
                      <VStack align={'start'} gap={0}>
                        <Text fontWeight={'semibold'}>{personName(engagement_0.founderUser, 'Founder')}</Text>
                        <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                          {engagement_0.company?.name || 'Startup'} · {engagement_0.category?.title}
                        </Text>
                        {engagement_0.invitedAt && <Text color={'colorPalette.fg/50'} fontSize={'xs'}>
                            Invited {formatWhen(engagement_0.invitedAt)}
                          </Text>}
                        {engagement_0.whyText && <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'} lineClamp={3} marginTop={1}>
                            {engagement_0.whyText}
                          </Text>}
                      </VStack>
                      <Badge colorPalette={'orange'} variant={'subtle'}>
                        Invited
                      </Badge>
                    </HStack>
                    <HStack gap={2} wrap={'wrap'}>
                      <Button loading={isBusy} size={'sm'} variant={'cta'} onClick={() => void handleResponse(id, 'ACCEPT')}>
                        Accept
                      </Button>
                      <Button disabled={isBusy} size={'sm'} variant={'outline'} onClick={() => void handleResponse(id, 'DECLINE')}>
                        Decline
                      </Button>
                      {engagement_0.founderUser?.slug && <Button asChild size={'sm'} variant={'ghost'}>
                          <NextLink href={`/members/profile/${engagement_0.founderUser.slug}`}>
                            View founder
                          </NextLink>
                        </Button>}
                    </HStack>
                  </Card.Body>
                </Card.Root>;
        })}
            <AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} />
          </VStack>}
      </AdvisorsQueryState>
    </>;
};

/** `/advisors/opportunities/invites` — pending board invites to accept or decline. */
export const OpportunitiesInvitesSection = () => {
  const $ = _c(3);
  const {
    counts
  } = useOpportunityCounts();
  const t0 = counts.invited > 0 ? "Founders who invited you to a board seat. Accepting activates the engagement." : "Founders who invite you to a board seat land here.";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <InvitesBody />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t0) {
    t2 = <OpportunitiesAccessGate><OpportunitiesLayout activeKey="invites" title="Board invites" description={t0}>{t1}</OpportunitiesLayout></OpportunitiesAccessGate>;
    $[1] = t0;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};